// Re-synced to dev-scrips-pm-react src/billing/components/add-payment-form.tsx (PROD-3312 appointment Billing tab, step 5):
// pm-react's field labels (a plain muted 12px label, sentence-case copy: "Patient payment", "Payment amount", "Payment
// date"), its `py-6` card, and its mount-time initialisers are kept as pm-react has them. Class translations: `bg-card` ->
// `bg-surface-card`; the spinner-hiding arbitrary variants -> the repo's `number-spinner-none`.
import { useState } from 'react';
// GAP: no DS icon for cash (Banknote), card payment (CreditCard) or wallet payment (Wallet); lucide in both modes (design sheet §3.1, logged not substituted).
import { Banknote, CreditCard, Building2, Wallet, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DatePickerField } from '@/shared/ui/components';
import { useAuthStore } from '@/auth/auth-store';
import { useAddPayment } from '@/billing/services/use-payments';
import { handlePaymentError } from '@/billing/lib/payment-errors';

import { formatMoney } from '@/shared/lib/money';
// PROD-2743 design sheet §3 (icons): DS names ON only (?ef=frameV2); OFF keeps today's lucide icon, unchanged.
/**
 * Add-payment form — the single shared surface for recording a patient payment,
 * used on **View Claim**, **appointment → billing**, and **patient-module
 * billing** so all three stay identical (previously the billing surfaces used a
 * cut-down modal that lacked the Balance method and the Pay-in-full/Other
 * toggle).
 *
 * Extracted verbatim from the curl-verified claim-review implementation. Wire
 * body → `AddPaymentRequest` → `POST /Billing/Payment/Create/Invoice/{invoiceId}`:
 *   { paymentMethod, paidAmount, isPaidFull, receivedBy(userId), note, receiptDate }
 *
 * `receiptDate` ships as no-Z ISO (`${date}T00:00:00.000`) — the billing-surface
 * convention (the backend treats it as cashier-shift local time). Cashier-shift
 * gate is reactive via `handlePaymentError` (matches Flutter — no auto-retry).
 *
 * The **Balance** method mirrors Flutter `add_payment.dart`: the tile shows the
 * patient's available balance when `availableBalance` is provided, and it is the
 * default method when a positive balance exists.
 */
export type PaymentMethod = 'Cash' | 'Card' | 'Bank' | 'Balance';

interface Props {
  invoiceId: string;
  /** Full patient-payable amount — the "Pay in full" value. */
  patientPayable: number;
  /** Outstanding cap (defensive amount clamp). Defaults to patientPayable. */
  patientOutstanding?: number;
  /** Patient's available credit balance — shows on the Balance tile + is the
   *  default method when > 0 (Flutter parity). Omit to hide the amount. */
  availableBalance?: number;
  /** Fired after a payment records successfully. */
  onSuccess?: () => void;
  /** Cashier-shift escalation — see `handlePaymentError`. */
  onCashierShiftRequired?: () => void;
  /** Approve-button label (two lines). Defaults to "Approve\nPayment". */
  approveLabel?: string;
}

function todayYmd(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function AddPaymentForm({
  invoiceId,
  patientPayable,
  patientOutstanding,
  availableBalance,
  onSuccess,
  onCashierShiftRequired,
  approveLabel = 'Approve\nPayment',
}: Props) {
  const userId = useAuthStore((s) => s.user?.userId);
  const addPayment = useAddPayment();

  const bal = availableBalance ?? 0;
  const hasBalance = bal > 0;
  // "Pay in full" pays the REMAINING outstanding, NOT the original payable —
  // Flutter `add_payment.dart` bases every amount on patientOutstanding
  // (patientPayable would over-charge once a partial payment exists).
  const remaining = patientOutstanding ?? patientPayable ?? 0;
  const cap = remaining;
  // Flutter initState default: the credit balance when it's a positive amount
  // ≤ what's owed, otherwise the full remaining outstanding.
  const initialAmount = hasBalance && bal <= remaining ? bal : remaining;

  const [method, setMethod] = useState<PaymentMethod>(hasBalance ? 'Balance' : 'Cash');
  // Flutter derives the Pay-in-full/Other state from `paidAmount == outstanding`.
  const [payInFull, setPayInFull] = useState(initialAmount === remaining);
  const [amount, setAmount] = useState<string>(initialAmount ? formatMoney(initialAmount) : '');
  const [date, setDate] = useState<string>(todayYmd());
  const [note, setNote] = useState('');

  const effectiveAmount = payInFull ? remaining : Number(amount);
  const canApprove =
    Boolean(invoiceId) &&
    Boolean(date) &&
    Number.isFinite(effectiveAmount) &&
    effectiveAmount > 0 &&
    !addPayment.isPending;

  const onPayInFull = (full: boolean) => {
    setPayInFull(full);
    if (full) setAmount(remaining ? formatMoney(remaining) : '');
  };

  // Flutter recomputes the amount when Balance is picked: clamp the credit to
  // the remaining outstanding (and never below 0).
  const onMethod = (m: PaymentMethod) => {
    setMethod(m);
    if (m === 'Balance') {
      const amt = bal >= remaining ? remaining : Math.max(bal, 0);
      setAmount(amt ? formatMoney(amt) : '');
      setPayInFull(amt === remaining);
    }
  };

  const onApprove = async () => {
    if (!canApprove) return;
    // Defensive clamp to the outstanding cap (a concurrent payment could leave
    // patientPayable stale between open + submit). Server is the final authority.
    const safeAmount = Number.isFinite(cap) ? Math.min(effectiveAmount, cap) : effectiveAmount;
    try {
      await addPayment.mutateAsync({
        invoiceId,
        request: {
          paymentMethod: method,
          paidAmount: safeAmount,
          isPaidFull: payInFull,
          receivedBy: userId,
          note: note || undefined,
          receiptDate: `${date}T00:00:00.000`,
        },
      });
      addPayment.reset?.();
      setNote('');
      onSuccess?.();
    } catch (err) {
      handlePaymentError(err, onCashierShiftRequired);
    }
  };

  return (
    <div className="bg-surface-card rounded-lg px-6 py-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
        <div>
          <FormLabel>Method</FormLabel>
          <div className="flex items-center gap-4">
            <MethodTile icon={<Banknote />} label="Cash" active={method === 'Cash'} onClick={() => onMethod('Cash')} />
            <MethodTile icon={<CreditCard />} label="Card" active={method === 'Card'} onClick={() => onMethod('Card')} />
            <MethodTile icon={<Building2 />} label="Bank" active={method === 'Bank'} onClick={() => onMethod('Bank')} />
            <MethodTile
              icon={<Wallet />}
              label={
                availableBalance != null ? (
                  <>
                    Balance
                    <br />
                    <span className="tabular-nums">({formatMoney(availableBalance)})</span>
                  </>
                ) : (
                  'Balance'
                )
              }
              active={method === 'Balance'}
              onClick={() => onMethod('Balance')}
            />
          </div>
        </div>

        <div>
          <FormLabel>Patient payment</FormLabel>
          <div className="flex items-center gap-4">
            <RadioTile label="Pay in full" active={payInFull} onClick={() => onPayInFull(true)} />
            <RadioTile label="Other" active={!payInFull} onClick={() => onPayInFull(false)} />
          </div>
        </div>

        <div>
          <FormLabel>Payment amount</FormLabel>
          <input
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={payInFull ? (remaining ? formatMoney(remaining) : '') : amount}
            onChange={(e) => setAmount(e.target.value)}
            disabled={payInFull}
            placeholder="0.00"
            className="w-full border border-border-default rounded-md px-3 py-2 text-sm text-text-primary bg-grey-100 tabular-nums focus:outline-none focus:ring-2 focus:ring-brand-primary disabled:cursor-not-allowed number-spinner-none"
          />
        </div>

        <div>
          <FormLabel>Payment date</FormLabel>
          <DatePickerField value={date} onChange={setDate} className="mt-1" />
        </div>

        <div className="md:col-span-2">
          <FormLabel>Note</FormLabel>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Enter note"
            className="w-full border border-border-default rounded-md px-3 py-2 text-sm text-text-primary bg-grey-100 focus:outline-none focus:ring-2 focus:ring-brand-primary"
          />
        </div>
      </div>

      <div className="mt-6 flex justify-start">
        <button
          type="button"
          onClick={onApprove}
          disabled={!canApprove}
          className="flex flex-col items-center gap-1 text-xs text-text-secondary hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-grey-100 border border-green-200 text-status-success">
            <CheckCircle2 />
          </span>
          <span className="text-center leading-tight whitespace-pre-line">
            {addPayment.isPending ? 'Approving…' : approveLabel}
          </span>
        </button>
      </div>
    </div>
  );
}


function MethodTile({
  icon,
  label,
  active = false,
  onClick,
}: {
  icon: React.ReactNode;
  label: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <button type="button" onClick={onClick} className="flex flex-col items-center gap-1 text-xs">
      <span
        className={cn(
          'inline-flex h-10 w-10 items-center justify-center rounded-md border transition-colors',
          active
            ? 'border-brand-primary text-brand-primary bg-blue-100/40'
            : 'border-border-default text-text-muted hover:border-grey-300',
        )}
      >
        {icon}
      </span>
      <span className={cn('text-center leading-tight', active ? 'text-brand-primary font-medium' : 'text-text-muted')}>
        {label}
      </span>
    </button>
  );
}

function RadioTile({
  label,
  active = false,
  onClick,
}: {
  label: string;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <button type="button" onClick={onClick} className="flex items-center gap-2 text-sm">
      <span
        className={cn(
          'inline-flex h-4 w-4 items-center justify-center rounded-full border-2',
          active ? 'border-brand-primary' : 'border-border-default',
        )}
      >
        {active ? <span className="h-2 w-2 rounded-full bg-brand-primary" /> : null}
      </span>
      <span className={active ? 'text-text-primary' : 'text-text-muted'}>{label}</span>
    </button>
  );
}

function FormLabel({ children }: { children: React.ReactNode }) {
  return <label className="text-xs font-medium text-text-muted mb-2 block">{children}</label>;
}
